import classNames from "classnames";
import React from "react";
import { Link } from "../../misc";
import { DraggingContext } from "../../utils";
import { LazyLoadImage } from "../../misc/LazyLoadImage/LazyLoadImage";
import { parseColourString } from "../../utils";
import * as WebRoutes from "@travellocal/utils/webRoutes";

import "./LocationCard.scss";

interface Props {
  /**
   * The location to display on this card.
   */
  location: ILocationSummary | ILocation;

  /**
   * Optional classname for the root element of the card.
   */
  className?: string;

  /**
   * Optional description, displayed below the location name.
   */
  description?: string;

  /**
   * Whether to use the high quality (large) or low quality (small) location hero image.
   */
  highQuality?: boolean;

  /**
   * Fallback URL for the background image, if the location does not have a hero image.
   */
  fallbackUrl?: string;

  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => unknown;
}

/**
 * Component to display a location card.
 * 
 * By default the location's hero image is the main feature of the card.
 *
 * Examples:
 * - One card per featured country.
 * - Result cards for "Where do you want to go?" search.
 * 
 * @status stable
 */
export const LocationCard = ({ className, description, fallbackUrl, highQuality, location, onClick }: Props) => {
  if (!location) {
    return null;
  }

  let backgroundColor = "var(--brand-accent)";
  let bgUrl = fallbackUrl || "/images/missing-image.jpg";
  let bgUrlMeta = "";
  if (location.heroImage) {
    bgUrl = highQuality
      ? location.heroImage.largeUrl
      : location.heroImage.smallUrl;

    if (location.heroImage.colour)
      backgroundColor = parseColourString(location.heroImage.colour) ?? backgroundColor;

    bgUrlMeta = location.heroImage.meta || "";
  }

  const headingClass = location.name.length <= 14 ? "heading--3" : "heading--4";

  return (
    <div className={classNames("location-card", className)}>
      <div className="image-container">
        <LazyLoadImage src={bgUrl} overlayStyle={{ backgroundColor }} alt={bgUrlMeta} />
      </div>
      <div className="content-container" style={{ backgroundColor }}>
        <h5 className={headingClass}>{location.name}</h5>
        <div>
          { description && <span>{description}</span> }
          <div className="find-out-more-container">
            <Link className="heading--4 text--light" to={WebRoutes.location(location)}>
              Find out more
            </Link>
          </div>
        </div>
      </div>

      <DraggingContext.Consumer>
        {props => <Link {...props} onClick={onClick} to={WebRoutes.location(location)} />}
      </DraggingContext.Consumer>
    </div>
  );
};
